import type {BehaviorEvent} from '../behaviors/behavior.types.event'
import {getDomNode} from '../dom-traversal/get-dom-node'
import {DOMEditor} from '../engine/dom/plugin/dom-editor'
import type {Path} from '../engine/interfaces/path'
import {isAncestorPath} from '../engine/path/is-ancestor-path'
import {rangeEdges} from '../engine/range/range-edges'
import {resolveSelection} from '../internal-utils/apply-selection'
import {getPointAtCoordinates} from '../internal-utils/point-at-coordinates'
import {getSelectionEndBlock, getSelectionStartBlock} from '../selectors'
import {getFragment} from '../selectors/selector.get-fragment'
import {getNodes} from '../traversal/get-nodes'
import type {PickFromUnion} from '../type-utils'
import type {EditorSelectionPoint} from '../types/editor'
import type {PortableTextEditorEngine} from '../types/editor-engine'
import type {EditorSnapshot} from './editor-snapshot'

export type EditorDom = {
  getBlockNodes: (snapshot: EditorSnapshot) => Array<Node>
  getChildNodes: (snapshot: EditorSnapshot) => Array<Node>
  getEditorElement: () => Element | undefined
  /**
   * The bounding rect of the snapshot's selection. A collapsed selection gives
   * a caret rect.
   */
  getSelectionRect: (snapshot: EditorSnapshot) => DOMRect | null
  /**
   * Resolves viewport (client) coordinates to the editor point where a click
   * at those coordinates would place the caret. The browser snaps to the
   * nearest position within the element at the coordinates. Returns `null`
   * when the coordinates don't resolve to a position inside the editor's
   * content.
   */
  getPointAtCoordinates: (coordinates: {
    x: number
    y: number
  }) => EditorSelectionPoint | null
  getStartBlockElement: (snapshot: EditorSnapshot) => Element | null
  getEndBlockElement: (snapshot: EditorSnapshot) => Element | null
  /**
   * Let the Editor set the drag ghost. This is to be sure that it will get
   * properly removed again when the drag ends.
   */
  setDragGhost: ({
    event,
    ghost,
  }: {
    event: PickFromUnion<BehaviorEvent, 'type', 'drag.dragstart'>
    ghost: {
      element: HTMLElement
      x: number
      y: number
    }
  }) => void
}

export function createEditorDom(
  sendBack: (event: {type: 'set drag ghost'; ghost: HTMLElement}) => void,
  editorEngine: PortableTextEditorEngine,
): EditorDom {
  return {
    getBlockNodes: (snapshot) => getBlockNodes(editorEngine, snapshot),
    getChildNodes: (snapshot) => getChildNodes(editorEngine, snapshot),
    getEditorElement: () => getEditorElement(editorEngine),
    getSelectionRect: (snapshot) => getSelectionRect(editorEngine, snapshot),
    getPointAtCoordinates: (coordinates) =>
      getPointAtCoordinates(editorEngine, coordinates),
    getStartBlockElement: (snapshot) =>
      getStartBlockElement(editorEngine, snapshot),
    getEndBlockElement: (snapshot) =>
      getEndBlockElement(editorEngine, snapshot),
    setDragGhost: ({event, ghost}) => setDragGhost({sendBack, event, ghost}),
  }
}

function getBlockNodes(
  editorEngine: PortableTextEditorEngine,
  snapshot: EditorSnapshot,
): Array<Node> {
  if (!snapshot.context.selection) {
    return []
  }

  try {
    const entries = getFragment(snapshot)

    return entries.flatMap((entry) => {
      const domNode = getDomNode(editorEngine, entry.path)
      return domNode ? [domNode] : []
    })
  } catch {
    return []
  }
}

function getChildNodes(
  editorEngine: PortableTextEditorEngine,
  snapshot: EditorSnapshot,
) {
  if (!snapshot.context.selection) {
    return []
  }

  try {
    const [start, end] = rangeEdges(
      snapshot.context.selection,
      editorEngine.snapshot.context,
    )
    const childEntries: Array<{node: unknown; path: Path}> = []
    let buffered: {node: unknown; path: Path} | undefined

    for (const entry of getNodes(editorEngine.snapshot, {
      from: start.path,
      to: end.path,
    })) {
      const entryPath = entry.path

      if (buffered) {
        if (isAncestorPath(buffered.path, entryPath)) {
          buffered = entry
          continue
        }

        childEntries.push(buffered)
      }

      buffered = entry
    }

    if (buffered) {
      childEntries.push(buffered)
    }

    return childEntries.flatMap((childEntry) => {
      const domNode = getDomNode(editorEngine, childEntry.path)

      if (!domNode) {
        return []
      }

      return domNode
    })
  } catch {
    return []
  }
}

function getEditorElement(editorEngine: PortableTextEditorEngine) {
  return getDomNode(editorEngine, [])
}

function getSelectionRect(
  editorEngine: PortableTextEditorEngine,
  snapshot: EditorSnapshot,
): DOMRect | null {
  const selection = snapshot.context.selection

  if (!selection) {
    return null
  }

  try {
    const engineRange = resolveSelection({snapshot}, selection)

    if (!engineRange) {
      return null
    }

    return DOMEditor.toDOMRange(
      editorEngine,
      engineRange,
    ).getBoundingClientRect()
  } catch {
    // `toDOMRange` throws when the selection isn't rendered (unmounted, virtualized).
    return null
  }
}

function getStartBlockElement(
  editorEngine: PortableTextEditorEngine,
  snapshot: EditorSnapshot,
) {
  const startBlock = getSelectionStartBlock(snapshot)

  if (!startBlock) {
    return null
  }

  const startBlockNode = getDomNode(editorEngine, startBlock.path)

  return startBlockNode instanceof Element ? startBlockNode : null
}

function getEndBlockElement(
  editorEngine: PortableTextEditorEngine,
  snapshot: EditorSnapshot,
) {
  const endBlock = getSelectionEndBlock(snapshot)

  if (!endBlock) {
    return null
  }

  const endBlockNode = getDomNode(editorEngine, endBlock.path)

  return endBlockNode instanceof Element ? endBlockNode : null
}

function setDragGhost({
  sendBack,
  event,
  ghost,
}: {
  sendBack: (event: {type: 'set drag ghost'; ghost: HTMLElement}) => void
  event: PickFromUnion<BehaviorEvent, 'type', 'drag.dragstart'>
  ghost: {
    element: HTMLElement
    x: number
    y: number
  }
}) {
  event.originEvent.dataTransfer.setDragImage(ghost.element, ghost.x, ghost.y)

  sendBack({
    type: 'set drag ghost',
    ghost: ghost.element,
  })
}
